/**
 * SG Entity 싱크 변경 이력 조회 캐시 — 같은 entity 에 대해 여러 곳에서 동시에 호출해도
 * 한 번만 네트워크 요청하도록 in-flight dedup + 짧은 TTL 캐시. (MY 와 동일 정책)
 */
import { entityApi } from '../api/entityApi';
import type { SgSyncHistoryGroup } from './syncHistoryAdapter';
import { isEntityChangeHistoryEnabled } from '../config/entityChangeHistoryFeature';

const TTL_MS = 15_000;

interface CacheEntry {
    at: number;
    promise: Promise<{ history: SgSyncHistoryGroup[] }>;
}

const cache = new Map<string, CacheEntry>();

/** API 응답 → history[] (EntitySyncHistoryModal 과 동일 unwrap) */
export function normalizeSyncHistoryGroups(res: unknown): SgSyncHistoryGroup[] {
    const root = (res as { data?: unknown })?.data ?? res;
    const history = Array.isArray((root as { history?: unknown })?.history)
        ? (root as { history: SgSyncHistoryGroup[] }).history
        : [];
    return [...history].sort((a, b) => {
        const ta = Date.parse(String(a?.comparisonDate || '')) || 0;
        const tb = Date.parse(String(b?.comparisonDate || '')) || 0;
        if (tb !== ta) return tb - ta;
        return (Number(b?.reportId) || 0) - (Number(a?.reportId) || 0);
    });
}

/** 캐시(또는 in-flight) 재사용하여 sync history 조회 */
export function fetchSyncHistoryCached(entityCd: string): Promise<{ history: SgSyncHistoryGroup[] }> {
    if (!isEntityChangeHistoryEnabled()) {
        return Promise.resolve({ history: [] });
    }

    const key = String(entityCd || '').trim();
    if (!key) return Promise.resolve({ history: [] });

    const now = Date.now();
    const hit = cache.get(key);
    if (hit && now - hit.at < TTL_MS) {
        return hit.promise;
    }

    const promise = entityApi
        .getEntitySyncHistory(key)
        .then((res) => ({ history: normalizeSyncHistoryGroups(res) }))
        .catch((e) => {
            cache.delete(key);
            throw e;
        });
    cache.set(key, { at: now, promise });
    return promise;
}

/** 싱크 실행/데이터 변경 후 강제 무효화 */
export function invalidateSyncHistory(entityCd?: string): void {
    if (entityCd) cache.delete(String(entityCd).trim());
    else cache.clear();
}
